export type ArrayMapCallback<T> = (item: T) => any;

/**
 * Use for native to improve performance
 * @param array
 * @param callback
 * @returns
 */
export function arrayMap<T>(array: T[], callback: ArrayMapCallback<T>): any[] {
  const length = array.length;
  const newArray = new Array(length);
  for (let i = 0; i < length; i++) {
    newArray[i] = callback(array[i]);
  }
  return newArray;
}

export type ArrayFilterCallback<T> = (item: T) => boolean | null | undefined;

/**
 * Use for native to improve performance
 * @param array
 * @param callback
 * @returns
 */
export function arrayFilter<T>(array: T[], callback: ArrayFilterCallback<T>, mapCallback?: ArrayMapCallback<T>): any[] {
  const length = array.length;
  const newArray = [];

  if (mapCallback) {
    for (let i = 0; i < length; i++) {
      if (callback(array[i])) {
        newArray.push(mapCallback(array[i]));
      }
    }
  } else {
    for (let i = 0; i < length; i++) {
      if (callback(array[i])) {
        newArray.push(array[i]);
      }
    }
  }

  return newArray;
}

export function arrayIndexFilter<T>(array: T[], callback: ArrayFilterCallback<T>): number[] {
  const length = array.length;
  const newArray = [];

  for (let i = 0; i < length; i++) {
    if (callback(array[i])) {
      newArray.push(i);
    }
  }

  return newArray;
}

/**
 * Use for native to improve performance
 * @param array
 * @returns
 */
export function arrayClone<T>(array: T[], isDeeply = false): T[] {
  const length = array.length;
  const newArray = new Array(length);
  for (let i = 0; i < length; i++) {
    if (isDeeply) {
      const itemString = JSON.stringify(array[i]);
      newArray[i] = JSON.parse(itemString);
    } else {
      newArray[i] = array[i];
    }
  }
  return newArray;
}

/**
 * Use for native to improve performance
 * @param array
 * @returns
 */
export function arraySlice<T>(array: T[], start: number, end: number, mapCallback?: ArrayMapCallback<T>): any[] {
  const maxLength = array.length;
  const endIndex = end <= maxLength ? end : maxLength;
  if (endIndex <= start) {
    return [];
  }
  const length = endIndex - start;

  const newArray = new Array(length);

  if (mapCallback) {
    for (let i = 0; i < length; i++) {
      newArray[i] = mapCallback(array[i + start]);
    }
  } else {
    for (let i = 0; i < length; i++) {
      newArray[i] = array[i + start];
    }
  }

  return newArray;
}

export function arrayUniquePush(array: any[], item: any, filterCallback?: ArrayFilterCallback<any>) {
  const length = array.length;

  if (filterCallback) {
    for (let i = 0; i < length; i++) {
      if (filterCallback(array[i])) {
        return;
      }
    }
  } else {
    for (let i = 0; i < length; i++) {
      if (array[i] === item) {
        return;
      }
    }
  }

  array.push(item);
}

export function arraySplitByFilter<T>(array: T[], callback: ArrayFilterCallback<T>): [Array<T>, Array<T>] {
  const trueArray: T[] = [];
  const falseArray: T[] = [];
  const length = array.length;
  for (let i = 0; i < length; i++) {
    if (callback(array[i])) {
      trueArray.push(array[i]);
    } else {
      falseArray.push(array[i]);
    }
  }
  return [trueArray, falseArray];
}

export function arraySplitIndexByFilter<T>(
  array: T[],
  callback: ArrayFilterCallback<T>,
): [Array<number>, Array<number>] {
  const trueArray: number[] = [];
  const falseArray: number[] = [];
  const length = array.length;
  for (let i = 0; i < length; i++) {
    if (callback(array[i])) {
      trueArray.push(i);
    } else {
      falseArray.push(i);
    }
  }
  return [trueArray, falseArray];
}

export type ArrayCompareEqualCallback<T> = (item1: T, item2: T) => boolean | null | undefined;

export function arrayUnique<T>(
  inputArray: T[],
  option?: { comparetor?: ArrayCompareEqualCallback<T>; lastChoice?: boolean },
): Array<T> {
  const comparetor = option?.comparetor ? option.comparetor : undefined;
  const lastChoice = option?.lastChoice ? option.lastChoice : false;
  const array = arrayClone(lastChoice ? inputArray.reverse() : inputArray);
  if (comparetor) {
    /**
     * Because directly remove item in array,
     * Get the length of each loop
     */
    for (let i = 0; i < array.length; i++) {
      const arrayLength = array.length;
      for (let j = arrayLength - 1; j > i; j--) {
        if (comparetor(array[i], array[j])) {
          array.splice(j, 1);
        }
      }
    }
  } else {
    /**
     * Because directly remove item in array,
     * Get the length of each loop
     */
    for (let i = 0; i < array.length; i++) {
      const arrayLength = array.length;
      for (let j = arrayLength - 1; j > i; j--) {
        if (array[i] === array[j]) {
          array.splice(j, 1);
          break;
        }
      }
    }
  }

  return array;
}

/**
 * @param array Array
 * @param item inserted item
 * @param postion position to inserted, default is first position
 * @returns array
 */
export function insertElement<T>(array: T[], item: T, postion = 0) {
  array.splice(postion, 0, item);
  return array;
}
